/** Small entry point: keep collection runtimes and template artwork dormant until approached. */
import { waitForFeatures, isHeroBlocking } from '@theme/storefront-loading';
const pending = new WeakSet();
const loaded = new WeakSet();
const waiting = new Set();
const waitTimers = new WeakMap();
const retryTimers = new WeakMap();
const attempts = new WeakMap();
const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
  for (const entry of entries) if (entry.isIntersecting) void activate(entry.target);
}, { rootMargin: '240px 0px' }) : null;

async function activate(section, explicit = false) {
  if (!section.isConnected || pending.has(section) || loaded.has(section)) return;
  if (!explicit && isHeroBlocking()) {
    if (!waiting.has(section)) {
      waiting.add(section);
      // A missing hero script must not block the rest of the storefront.
      waitTimers.set(section, setTimeout(() => void activate(section, true), 6000));
    }
    return;
  }
  waiting.delete(section);
  clearTimeout(waitTimers.get(section));
  clearTimeout(retryTimers.get(section));
  pending.add(section);
  try {
    await waitForFeatures();
    if (!section.isConnected) return;
    section.setAttribute('data-cover-runtime-ready', '');
    const attempt = attempts.get(section) || 0;
    attempts.set(section, attempt + 1);
    let timeout;
    try {
      await Promise.race([
        Promise.all([section.dataset.coverRuntime, section.dataset.parallaxRuntime]
          .filter(Boolean).map(source => {
            const url = new URL(source, document.baseURI);
            // A rejected module import is cached by the browser. A bounded retry
            // needs a fresh entry URL, while shared dependencies stay versioned.
            if (attempt) url.searchParams.set('cover_retry', String(attempt));
            return import(url.href);
          })),
        new Promise((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Collection runtime load timed out')), 8000);
        }),
      ]);
    } finally { clearTimeout(timeout); }
    if (!section.isConnected) return;
    section.activate?.();
    section.querySelector(':scope > collection-parallax-cover')?.activate?.();
    loaded.add(section);
    attempts.delete(section);
    observer?.unobserve(section);
  } catch (error) {
    // Retry transient failures without requiring the shopper to leave and
    // re-enter the viewport. Never leave the pending latch set indefinitely.
    console.warn('Collection runtime could not load', error);
    if (section.isConnected && (attempts.get(section) || 0) < 3) {
      retryTimers.set(section, setTimeout(() => void activate(section, true), 1000));
    }
  } finally {
    pending.delete(section);
  }
}

function discover(root = document) {
  const sections = [...root.querySelectorAll('collection-cover-carousel[data-cover-runtime]')];
  for (const section of sections) {
    if (window.Shopify?.designMode || !observer) void activate(section);
    else observer.observe(section);
  }
}

for (const type of ['pointerover', 'pointerdown', 'focusin']) {
  document.addEventListener(type, event => {
    const section = event.target instanceof Element && event.target.closest('collection-cover-carousel[data-cover-runtime]');
    if (section) void activate(section, true);
  }, { passive: true });
}
const releaseWaiting = () => {
  if (isHeroBlocking()) return;
  for (const section of [...waiting]) void activate(section);
};
document.addEventListener('hero:intro-settled', releaseWaiting);
const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
connection?.addEventListener?.('change', releaseWaiting);
let scrollFrame = 0;
document.addEventListener('scroll', () => {
  if (!waiting.size || scrollFrame) return;
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = 0;
    for (const section of [...waiting]) {
      const rect = section.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top < innerHeight + 240) void activate(section, true);
    }
  });
}, { capture: true, passive: true });
document.addEventListener('shopify:section:load', event => discover(event.target));
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('collection-cover-carousel[data-cover-runtime]').forEach(section => {
    observer?.unobserve(section);
    waiting.delete(section);
    clearTimeout(waitTimers.get(section));
    clearTimeout(retryTimers.get(section));
    attempts.delete(section);
  });
});
discover();
